import { isVideogameWithMicromarkets } from 'app/config/micromarkets.config.helper';
import { PLAYER_MARKET_CATEGORY } from 'app/constants/markets';
import { sortBy, listWithMinimumLength } from 'app/utils/array';
import { hasAccessToFeature } from 'app/utils/permissions';
import { assertNever } from 'app/utils/types';

interface GameWithWinner extends PandapiGameBase {
  winnerOpponent?: PandapiOpponent;
}

function isTeam(opponent: PandapiTeam | PandapiPlayer): opponent is PandapiTeam {
  return (opponent as any).acronym !== undefined;
}

function hasParticipantValue(
  value: GameSettlement['value']
): value is GameSettlementParticipantValue[] {
  return Array.isArray(value as any);
}

function getOpponentAccronym(opponent: PandapiTeam | PandapiPlayer | undefined): string {
  if (!opponent) {
    return 'TBD';
  }
  if (isTeam(opponent)) {
    if (opponent.acronym) return opponent.acronym.substring(0, 3);
  } else if (opponent.name === 'To be determined') {
    return 'TBD';
  }
  return opponent.name.substring(0, 3).toUpperCase();
}

function isEventStarted({ status }: { status: MatchStatus | GameStatus }): boolean {
  return ['running', 'live', 'finished', 'settled'].includes(status);
}

function isAllMarketActionsDisplay({ status }: { status: MatchStatus | GameStatus }): boolean {
  return ['pre_match', 'running', 'live', 'finished', 'canceled', 'postponed'].includes(status);
}

function countWonGames(
  opponent: PandapiTeam | PandapiPlayer,
  { games, game_advantage }: { games: PandapiGame[]; game_advantage: number | null }
) {
  const scoreAdvantage = game_advantage === opponent.id ? 1 : 0;
  return (games as PandapiGameBase[]).reduce((result, game) => {
    if (game.winner.id === opponent.id) {
      return result + 1;
    }
    return result;
  }, scoreAdvantage);
}

function isMatchPlayingOrNext(match: PandapiMatch, now = new Date()): boolean {
  return match.status === 'live' || (match.begin_at ? new Date(match.begin_at) > now : false);
}

function findPlayingGame(match: PandapiMatch): PandapiGameBase | undefined {
  const now = new Date().toISOString();
  return match.games.find(
    game => !!game.begin_at && game.begin_at < now && game.status !== 'finished'
  );
}

function getMatchGames({ games, opponents }: PandapiMatch): GameWithWinner[] {
  return sortBy(games, 'position').map(game => {
    if (game.winner.id) {
      const winnerOpponent = opponents.find(({ opponent }) => opponent.id === game.winner.id);
      if (winnerOpponent) {
        return { ...game, winnerOpponent };
      }
    }

    return game;
  });
}

function getGameStatus(match: PandapiMatch, { status }: PandapiGame): MatchStatus {
  if (match.status === 'canceled') {
    return 'canceled';
  }

  switch (status) {
    case 'not_started':
      switch (match.status) {
        case 'not_booked':
        case 'set_up':
        case 'pending':
        case 'postponed':
        case 'pre_match':
        case 'settled':
          return match.status;
        case 'live':
        case 'finished':
          return 'pre_match';
        default:
          assertNever(match.status);
      }
    case 'running':
      return 'live';
    case 'finished':
      if (match.status === 'settled') {
        return 'settled';
      }
      return 'finished';
    case 'not_played':
      return 'not_booked';
    default:
      assertNever(status);
  }
}

function createNotDeterminedTeam(id: number): PandapiTeam {
  return {
    id,
    name: 'To be determined',
    slug: null,
    acronym: 'TBD',
    image_url: null
  };
}

function createNotDeterminedPlayer(id: number): PandapiPlayer {
  return {
    id,
    slug: null,
    name: 'To be determined',
    first_name: null,
    last_name: null,
    hometown: null,
    role: null,
    image_url: null
  };
}

function getOpponentsWithNotDefined<T extends PandapiTeam | PandapiPlayer>(
  type: WinnerType,
  opponents: T[],
  { nbMinimumTeams } = { nbMinimumTeams: 2 }
): T[] {
  const teamsWithNotDefined: T[] = listWithMinimumLength(opponents, {
    minLength: nbMinimumTeams,
    createItem: index => {
      const id = -index - opponents.length;
      switch (type) {
        case 'Team':
          return createNotDeterminedTeam(id) as T;
        case 'Player':
          return createNotDeterminedPlayer(id) as T;
        default:
          assertNever(type);
      }
    }
  });

  // use condition instead of switch because only team have this specific behavior with acronym
  if (type === 'Team') {
    return (teamsWithNotDefined as PandapiTeam[]).map(team => ({
      ...team,
      acronym: getOpponentAccronym(team)
    })) as T[];
  }

  return teamsWithNotDefined;
}

function getMatchOpponents(
  match: { tournament: { winner_type: WinnerType }; opponents: PandapiOpponent[] },
  options = { nbMinimumTeams: 2 }
): (PandapiTeam | PandapiPlayer)[] {
  const opponents = match.opponents.map(({ opponent }) => opponent);

  return getOpponentsWithNotDefined(match.tournament.winner_type, opponents, options);
}

function isMatchBooked({ betting_metadata }: Omit<PandapiMatch, 'opponents'>): boolean {
  return !!betting_metadata && !!betting_metadata.booked;
}

function isMatchNeedReviewed(match: Omit<PandapiMatch, 'opponents'>, userType: UserType): boolean {
  return (
    (hasAccessToFeature('TRADER_INPUT', userType) && // only pandascore user can review a match
      !!match.betting_metadata &&
      !!match.betting_metadata.booked &&
      !!match.betting_metadata.markets_created &&
      !match.betting_metadata.reviewed) ||
    (hasAccessToFeature('TRADER_INPUT', userType) && // only pandascore user can review a match
      match.status === 'postponed' &&
      !!match.betting_metadata &&
      !!match.betting_metadata.waiting_for_postponed_review)
  );
}

function isMatchFinished({ status }: Omit<PandapiMatch, 'opponents'>): boolean {
  return status === 'finished' || status === 'settled';
}

function isMatchCanceled({ status }: Omit<PandapiMatch, 'opponents'>): boolean {
  return status === 'canceled';
}

function isMatchPostponed({ status }: Omit<PandapiMatch, 'opponents'>): boolean {
  return status === 'postponed';
}

function getMatchBlueprint({ betting_metadata }: Omit<PandapiMatch, 'opponents'>): string {
  return betting_metadata && betting_metadata.blueprint ? betting_metadata.blueprint.name : '';
}

function isMarketSuspendable({ mostBalancedMarket }: MarketBlock): boolean {
  return mostBalancedMarket?.status === 'active' || mostBalancedMarket?.status === 'suspended';
}

const isMarketClosable = isMarketSuspendable;

function areAllMarketsSuspended(marketBlocks: MarketBlock[]): boolean {
  const allRegularMarkets = marketBlocks.filter(
    ({ mostBalancedMarket }) => !mostBalancedMarket.is_micromarket
  );
  const suspendableMarkets = allRegularMarkets.filter(isMarketSuspendable);
  if (suspendableMarkets.length === 0) {
    return false;
  }

  return suspendableMarkets.every(
    ({ mostBalancedMarket }) => mostBalancedMarket?.status === 'suspended'
  );
}

function areAllPlayerMarketsSuspended(
  marketBlocks: MarketBlock[],
  eventId: number | null
): boolean {
  const suspendableMarkets = marketBlocks.filter(
    market =>
      isMarketSuspendable(market) &&
      market.category === PLAYER_MARKET_CATEGORY &&
      market.mostBalancedMarket.event_id === eventId
  );

  if (suspendableMarkets.length === 0) {
    return false;
  }

  return suspendableMarkets.every(
    ({ mostBalancedMarket }) =>
      mostBalancedMarket?.status === 'suspended' || mostBalancedMarket?.status === 'deactivated'
  );
}

function aggregateMatchMarkets(markets: MatchMarketBlocks): MarketBlock[] {
  const marketsBlocks = markets.market_blocks ? [...markets.market_blocks] : [];

  markets.games.forEach(game => {
    if (game.market_blocks) {
      // mutate marketsBlocks for performance, this function can be run often because of websockets
      game.market_blocks.forEach(marketBlock => marketsBlocks.push(marketBlock));
    }
  });

  return marketsBlocks;
}

function hasMatchMarkets(markets: MatchMarketBlocks): boolean {
  return (
    markets?.market_blocks.length > 0 ||
    markets?.games.some(({ market_blocks }) => market_blocks?.length > 0)
  );
}

function isMatchMarketSuspended(markets: MatchMarketBlocks): boolean {
  const marketsBlocks = aggregateMatchMarkets(markets);
  return areAllMarketsSuspended(marketsBlocks);
}

function isMatchPlayerMarketsSuspended(
  markets: MatchMarketBlocks,
  eventId: number | null
): boolean {
  const marketsBlocks = aggregateMatchMarkets(markets);
  return areAllPlayerMarketsSuspended(marketsBlocks, eventId);
}

function isMatchMarketSuspendable(markets: MatchMarketBlocks): boolean {
  const marketsBlocks = aggregateMatchMarkets(markets);
  return marketsBlocks.some(isMarketSuspendable);
}

function isMatchPlayerMarketsSuspendable(markets: MatchMarketBlocks, eventId: number): boolean {
  const marketsBlocks = aggregateMatchMarkets(markets);
  return marketsBlocks.some(
    market =>
      market.category === PLAYER_MARKET_CATEGORY &&
      market.mostBalancedMarket.event_id === eventId &&
      (market.mostBalancedMarket?.status === 'active' ||
        market.mostBalancedMarket?.status === 'suspended')
  );
}

function isMatchMarketActivable(markets: MatchMarketBlocks): boolean {
  const marketsBlocks = aggregateMatchMarkets(markets);
  const notSettledMarkets = marketsBlocks.filter(
    ({ mostBalancedMarket }) => mostBalancedMarket?.status !== 'settled'
  );

  return (
    notSettledMarkets.length > 0 &&
    notSettledMarkets.every(
      ({ mostBalancedMarket }) => mostBalancedMarket?.status === 'deactivated'
    )
  );
}

function canMatchHaveMarkets(match: PandapiMatch): boolean {
  // so a match without betting_metadata can have markets
  return ['set_up', 'pending', 'pre_match', 'live'].includes(match.status);
}

function displayTraderInputsInList({ betting_metadata }: PandapiMatch): boolean {
  return !!betting_metadata?.booked && !betting_metadata.settled && betting_metadata.inputs_enable;
}

function matchTradableByGames(
  match: PandapiMatch,
  traderInputs: TraderInputs | undefined
): boolean {
  if (!match.betting_metadata?.inputs_enable || !traderInputs) {
    return false;
  }

  const matchStatus: MatchStatus[] = [
    'set_up',
    'pre_match',
    'live',
    'finished',
    'settled',
    'canceled',
    'postponed'
  ];
  return matchStatus.includes(match.status);
}

function disabledTradeGameSeparately(activate: boolean, match: PandapiMatch): boolean {
  const matchStatus: MatchStatus[] = ['set_up', 'pre_match', 'live'];

  if (matchStatus.includes(match.status) && !activate) {
    return false;
  }

  return true;
}

function startableByTrader(match: PandapiMatch): boolean {
  return Boolean(
    match.betting_metadata?.reviewed && match.betting_metadata?.coverage === 'live_odds'
  );
}

function isWidgetVisible(match: PandapiMatch): boolean {
  return match.status !== 'finished' && match.status !== 'settled';
}

function getOpponentWithAdvantage(match: PandapiMatch): PandapiTeam | PandapiPlayer | null {
  if (match.game_advantage !== null) {
    const opponent = match.opponents.find(({ opponent }) => opponent.id === match.game_advantage);

    if (opponent) {
      return opponent.opponent;
    }
  }

  return null;
}

function getPlayersIdsFromRosters(rosters: PandapiOpponents): number[] {
  if (rosters.opponent_type === 'Team') {
    return rosters.opponents.map(roster => roster.players.map(player => player.id)).flat();
  }

  if (rosters.opponent_type === 'Player') {
    return rosters.opponents.map(roster => roster.id);
  }

  return [];
}

const getOpponentFromParticipant = (
  participantId: number | null,
  participantType: WinnerType | null,
  rosters: PandapiOpponents | undefined
): PandapiTeamWithRoster | null => {
  if (rosters && rosters.opponent_type === 'Team') {
    if (participantType === 'Player') {
      return getOpponentFromPlayerId(participantId, rosters.opponents) ?? null;
    }
    return rosters.opponents.find(opponent => opponent.id === participantId) ?? null;
  }
  return null;
};

function getOpponentFromPlayerId(
  playerId: number | null,
  rosters: PandapiTeamWithRoster[]
): PandapiTeamWithRoster | undefined {
  return rosters.find(roster => roster.players.find(player => player.id === playerId));
}

function isGameForfeitable(match: PandapiMatch): boolean {
  if (match.number_of_games > 1 && match.status !== 'pre_match') {
    return true;
  }

  return false;
}

const canMicrobettingBeAvailable = (match: PandapiMatch): boolean =>
  Boolean(
    match.betting_metadata?.coverage === 'live_odds' && // is live available
      isVideogameWithMicromarkets(match.videogame.slug)
  );

const isMatchMicrobettingAvailable = (match: PandapiMatch): boolean =>
  canMicrobettingBeAvailable(match) &&
  Boolean(
    ['live', 'finished', 'settled'].includes(match.status) && // is live, finished or settled
      match.betting_metadata?.micromarkets_enabled
  );

const isGameMicrobettingAvailable = (game: PandapiGame | undefined): boolean =>
  Boolean(game && ['finished', 'running', 'not_started'].includes(game.status));

const isMatchMicrobettingDemoAvailable = (match: PandapiMatch, userType: UserType): boolean =>
  !hasAccessToFeature('MICROBETTING', userType) &&
  canMicrobettingBeAvailable(match) &&
  Boolean(
    match.status === 'live' && // is live
      match.betting_metadata?.micromarkets_enabled
  );

const getBlacklistMatchSituation = (
  playerList: BlacklistPlayer[] | undefined
): { watchlist: boolean; blacklist: boolean } => {
  if (!playerList) {
    return { blacklist: false, watchlist: false };
  }

  return {
    blacklist: playerList.some(player => player.status === 'blacklist'),
    watchlist: playerList.some(player => player.status === 'watchlist')
  };
};
const isBetbuilderAvailableOnCurrentVideogame = (
  videogame: VideogameSlug,
  betbuilderVideogames: BetbuilderVideogame[] | undefined
) => betbuilderVideogames && betbuilderVideogames.map(game => game.slug).includes(videogame);

export type { GameWithWinner };
export {
  isTeam,
  hasParticipantValue,
  getOpponentAccronym,
  isEventStarted,
  countWonGames,
  findPlayingGame,
  getMatchGames,
  getGameStatus,
  getOpponentsWithNotDefined as getTeamsWithNotDefined,
  getMatchOpponents,
  isMatchBooked,
  isMatchNeedReviewed,
  isMatchFinished,
  getMatchBlueprint,
  areAllMarketsSuspended,
  isMarketSuspendable,
  isMarketClosable,
  hasMatchMarkets,
  isMatchMarketSuspended,
  isMatchMarketSuspendable,
  isMatchMarketActivable,
  canMatchHaveMarkets,
  displayTraderInputsInList,
  matchTradableByGames,
  startableByTrader,
  isWidgetVisible,
  getOpponentWithAdvantage,
  isAllMarketActionsDisplay,
  disabledTradeGameSeparately,
  isMatchCanceled,
  isMatchPostponed,
  getPlayersIdsFromRosters,
  getOpponentFromParticipant,
  getOpponentFromPlayerId,
  isMatchPlayerMarketsSuspended,
  isMatchPlayerMarketsSuspendable,
  isGameForfeitable,
  isMatchPlayingOrNext,
  isMatchMicrobettingAvailable,
  isGameMicrobettingAvailable,
  isMatchMicrobettingDemoAvailable,
  aggregateMatchMarkets,
  getBlacklistMatchSituation,
  canMicrobettingBeAvailable,
  isBetbuilderAvailableOnCurrentVideogame
};
